import React, {ChangeEvent, useEffect, useState} from 'react';
import {Link, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import styles from './global-player.module.scss';
import ProgressBar from "@/components/global/posts/player/progress-bar";
import {PLAYERSTATUS} from "@/constants";
import VolumeControl from "@/components/global/posts/player/volume-control";
import PlayButton from "@/components/global/posts/player/play-button";
import {closeGlobalPlayer, pause, setGlobalPlayer, setGlobalPlayerFile, setGlobalPlayerStatus} from "@/actions/player";
import * as playerHelper from "@/app/player";
import Like from "@/components/global/like";

const GlobalPlayer: React.FC = () => {
  const file = useSelector((state: any) => state.player.file) as IFileItem | undefined;
  const post = useSelector((state: any) => state.player.post) as IPostItem | IPostDetails | undefined;
  const playerStatus = useSelector((state: any) => state.player.status) as string;
  const player = useSelector((state: any) => state.player.player) as HTMLAudioElement | undefined;
  const dispatch = useDispatch();

  const [playerCollapse, setPlayerCollapse] = useState(false);
  const [currentTime, setCurrentTime] = useState<string>("00:00");
  const [totalTime, setTotalTime] = useState<string>("00:00");
  const [dataArray, setDataArray] = useState<Float32Array>();
  const [bgColor, setBgColor] = useState<string>('var(--bs-primary)');

  const setPlayerStatus = (ps: string) => {
    setGlobalPlayerStatus(ps)(dispatch);
  }

  useEffect(() => {
    return playerHelper.initializePlayer({ setPlayerStatus });
  }, []);

  useEffect(() => {
    return playerHelper.setFile({ setPlayerStatus, setCurrentTime, setTotalTime, file });
  }, [file]);

  useEffect(() => {
    return playerHelper.playerStatusChanged({ player, playerStatus, setDataArray });
  }, [playerStatus]);

  useEffect(() => {
    return playerHelper.listenTimeUpdateEvent({ setCurrentTime, setTotalTime, setPlayerStatus, player: playerHelper.gPlayer, playerStatus });
  }, [playerStatus]);

  useEffect(
      () => {
        return playerHelper.dataArrayChanged({ setBgColor, playerStatus, dataArray })
      },
      [dataArray]);

  const closePlayer = () => {
    closeGlobalPlayer()(dispatch);
  };

  const selectedFileChanged = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedFile = post?.files.filter(f => f.fileSrc === e.target.value)[0] as IFileItem;
    setGlobalPlayerFile(selectedFile)(dispatch);
  }

  return <>
      {!post || !file || !playerHelper.gPlayer || playerStatus === PLAYERSTATUS.CLOSED ? <></> : (
      <div className={styles.playerContainer + (playerCollapse ? ' ' + styles.collapsed : '')}>
        <span className={styles.toggler} onClick={() => setPlayerCollapse(!playerCollapse)} />
        <div className={styles.header}>
          <ProgressBar setPlayerStatus={setPlayerStatus} playerStatus={playerStatus} player={playerHelper.gPlayer} currentTime={currentTime} totalTime={totalTime} />
        </div>
        <div className={styles.body}>
          <div>
            <div className={styles.close} onClick={closePlayer}>
              <i className="far fa-times" />
            </div>
            <div className={styles.postInfo + ' d-none d-lg-flex'}>
              <div className={styles.postImage}><Link to={`/post/${post.slug}`}><img src={post.image} alt={post.title} /></Link></div>
              <div className={styles.postDescription}>
                <div className={styles.postTitle}><Link to={`/post/${post.slug}`}>{post.title}</Link></div>
                <div className={styles.artist}>
                  <Link to={'/artist/' + post.artist.id}>
                    {post.artist.hasBadge ? (
                        <span className="text-secondary ms-2 d-flex">
                        <i className="fas fa-badge-check" />
                      </span>
                    ) : ''} {' '}
                    <span>{post.artist.name}</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={styles.buttonContainer}>
              <div className={styles.forward} onClick={playerHelper.forward(playerHelper.gPlayer)}>
                <i className="fa fa-step-forward" />
              </div>
              <PlayButton bgColor={bgColor} setPlayerStatus={setPlayerStatus} player={playerHelper.gPlayer} playerStatus={playerStatus} />
              <div className={styles.backward} onClick={playerHelper.backward(playerHelper.gPlayer)}>
                <i className="fa fa-step-backward" />
              </div>
            </div>
          </div>
          <div>
            <div className={'me-lg-4'}>
              <VolumeControl player={playerHelper.gPlayer} />
            </div>
            <div className={styles.playerInfo + ' rtl d-none d-lg-flex'}>
              <i className="fa fa-music-alt" />
              <select defaultValue={file.fileSrc} onChange={selectedFileChanged}>
                {post.files.filter(f => f.isPlayable).map(f => (<option key={f.fileSrc} value={f.fileSrc}>{f.title}</option>))}
              </select>
            </div>
            <div className={styles.like + ' d-none d-sm-flex'}>
              <Like post={post} />
            </div>
          </div>
        </div>
      </div>)}
  </>
}

export default GlobalPlayer;
